import { Box } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { DotLegalButton, DotLegalCheckbox, DotLegalSearchField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import PaneLayout from "../../../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../../../main/panes/definePane";
import { useTableDisplayFieldsPane } from "./TableDisplayFieldsPane.hooks";
import { TableDisplayField, TableDisplayFieldsPaneProps } from "./TableDisplayFieldsPane.types";
import { useTableDisplayFieldsPaneStyles } from "./TableDisplayFieldsPane.styles";

function TableDisplayFieldsPane(props: TableDisplayFieldsPaneProps) {
    const { translateString } = useTranslation();
    const styles = useTableDisplayFieldsPaneStyles();
    const {
        searchInput,
        setSearchInput,
        shownFields,
        hiddenFieldGroups,
        renderSelectedField,
        onFieldToggle,
        onDragEnd,
        resetText,
        onResetClick,
        onApplyClick,
    } = useTableDisplayFieldsPane(props);

    return (
        <PaneLayout
            header={translateString("columns")}
            fixedContent={
                <DotLegalSearchField labelSearch={translateString("findColumn")} value={searchInput} onChange={setSearchInput} useFullWidth autoFocus />
            }
            okButtonText={translateString("apply")}
            onOkClick={onApplyClick}
            footerStart={
                resetText && (
                    <DotLegalButton buttonType="linkButton" size="small" onClick={onResetClick}>
                        {resetText}
                    </DotLegalButton>
                )
            }
        >
            <Box sx={styles.sectionHeading}>
                {translateString("shownColumns")}
                <Box component="span" sx={styles.sectionHint}>
                    {translateString("dragToReorder")}
                </Box>
            </Box>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="shownFields">
                    {(droppable) => (
                        <Box ref={droppable.innerRef} {...droppable.droppableProps}>
                            {shownFields.map((field, index) => (
                                <ShownFieldRow
                                    key={field.id}
                                    field={field}
                                    index={index}
                                    customContent={renderSelectedField?.(field.id, field.name)}
                                    onToggle={onFieldToggle}
                                />
                            ))}
                            {droppable.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>

            {hiddenFieldGroups.length > 0 && (
                <Box sx={styles.hiddenSection}>
                    <Box sx={styles.sectionHeading}>{translateString("hiddenColumns")}</Box>
                    {hiddenFieldGroups.map((group) => (
                        <Box key={group.grouping}>
                            <Box sx={styles.groupHeading}>{group.name}</Box>
                            {group.fields.map((field) => (
                                <Box key={field.id} sx={styles.fieldRow}>
                                    <Box sx={styles.gripSpace} />
                                    <FieldCheckbox field={field} checked={false} onToggle={onFieldToggle} />
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            )}
        </PaneLayout>
    );
}

interface ShownFieldRowProps {
    field: TableDisplayField;
    index: number;
    customContent: JSX.Element | undefined;
    onToggle: (fieldId: string) => void;
}

function ShownFieldRow({ field, index, customContent, onToggle }: ShownFieldRowProps) {
    const { translateString } = useTranslation();
    const styles = useTableDisplayFieldsPaneStyles();

    return (
        <Draggable draggableId={field.id} index={index}>
            {(draggable, snapshot) => (
                <Box ref={draggable.innerRef} {...draggable.draggableProps} sx={[styles.fieldRow, snapshot.isDragging && styles.draggingFieldRow]}>
                    <Box sx={styles.grip} {...draggable.dragHandleProps}>
                        <DragIndicatorIcon fontSize="small" />
                    </Box>
                    {customContent ? (
                        <>
                            <Box sx={styles.fieldLine}>
                                <DotLegalCheckbox checked onChange={() => onToggle(field.id)} disabled={field.isMandatory} />
                            </Box>
                            <Box sx={styles.fieldContent}>{customContent}</Box>
                        </>
                    ) : (
                        <FieldCheckbox field={field} checked onToggle={onToggle} />
                    )}
                    {field.isMandatory && <Box sx={styles.alwaysShown}>{translateString("alwaysShown")}</Box>}
                </Box>
            )}
        </Draggable>
    );
}

interface FieldCheckboxProps {
    field: TableDisplayField;
    checked: boolean;
    onToggle: (fieldId: string) => void;
}

function FieldCheckbox({ field, checked, onToggle }: FieldCheckboxProps) {
    const styles = useTableDisplayFieldsPaneStyles();

    return (
        <Box sx={styles.labelledFieldLine}>
            <DotLegalCheckbox checked={checked} onChange={() => onToggle(field.id)} disabled={field.isMandatory} label={field.name} tooltip={field.tooltip} />
        </Box>
    );
}

export default definePane({ slot: "right", component: TableDisplayFieldsPane });
